import { cn } from '@client/lib';
import { ReactNode } from 'react';

export interface PreferenceOption<T extends string> {
  value: T;
  title: string;
  description?: string;
}

interface PreferenceQuestionProps<T extends string> {
  icon: ReactNode;
  title: string;
  description: string;
  options: PreferenceOption<T>[];
  selectedValue: T | null;
  onSelect: (value: T) => void;
  className?: string;
}

export function PreferenceQuestion<T extends string>({
  icon,
  title,
  description,
  options,
  selectedValue,
  onSelect,
  className,
}: PreferenceQuestionProps<T>) {
  return (
    <div className={cn('flex flex-col items-center text-center', className)}>
      {/* Icon */}
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100 text-gray-600">
        {icon}
      </div>

      {/* Title */}
      <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">{title}</h2>

      {/* Description */}
      <p className="mx-auto mt-3 max-w-lg text-base text-gray-700">{description}</p>

      {/* Options */}
      <div className="mt-8 w-full max-w-lg space-y-3">
        {options.map(option => {
          const isSelected = selectedValue === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={cn(
                'flex w-full items-center justify-between rounded-xl border-2 px-5 py-4 text-left transition-all',
                {
                  'border-primary bg-primary/5': isSelected,
                  'border-gray-200 bg-white hover:border-gray-300': !isSelected,
                },
              )}
            >
              <div>
                <span
                  className={cn('block font-semibold', {
                    'text-gray-900': isSelected,
                    'text-gray-700': !isSelected,
                  })}
                >
                  {option.title}
                </span>
                {option.description && (
                  <span className="mt-0.5 block text-sm text-gray-500">{option.description}</span>
                )}
              </div>

              {/* Radio indicator */}
              <div
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                  {
                    'border-primary': isSelected,
                    'border-gray-300': !isSelected,
                  },
                )}
              >
                {isSelected && <div className="bg-primary h-2.5 w-2.5 rounded-full" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface PreferencePaginationProps {
  currentQuestion: number;
  totalQuestions: number;
  className?: string;
}

export function PreferencePagination({
  currentQuestion,
  totalQuestions,
  className,
}: PreferencePaginationProps) {
  return (
    <div className={cn('flex items-center justify-center gap-2', className)}>
      {Array.from({ length: totalQuestions }).map((_, index) => (
        <div
          key={index}
          className={cn('h-2 rounded-full transition-all', {
            'w-6 bg-primary': index + 1 === currentQuestion,
            'w-2 bg-gray-300': index + 1 !== currentQuestion,
          })}
        />
      ))}
    </div>
  );
}

